/**
 * Health Check-Up Packages — four tiers, each with shared test groups and
 * optional male / female add-ons. No prices: quotes are confirmed per case.
 */

export const PACKAGE_DISCLAIMER =
  'Final tests depend on the attending doctor\u2019s assessment, hospital availability and confirmation of your programme. Lists shown are indicative and may be adjusted for age, medical history and clinical need.';

export const HEALTH_CHECKUP_PACKAGES = [
  {
    slug: 'standard',
    name: 'Standard',
    tier: 1,
    tagline: 'Essential annual check-up',
    summary:
      'Core consultations, vital checks, specialist reviews, baseline blood tests and a chest X-ray — a practical first look at overall health.',
    bestFor: ['Adults under 40 without known conditions', 'First-time health screening', 'Short half-day visit'],
    duration: 'Approx. half-day',
    highlights: ['GP consultation', 'Eye, dental, ENT & skin review', 'Blood tests & chest X-ray'],
    genders: ['male', 'female'],
    sections: [
      {
        title: 'Consultations & vital checks',
        items: [
          'General physician consultation and medical history review',
          'Vital checks — height, weight, BMI, blood pressure, pulse',
          'Eye examination (visual acuity)',
          'Dental examination',
          'Dermatology consultation',
          'ENT (ear, nose & throat) consultation',
          'General surgical review',
        ],
      },
      {
        title: 'Blood & urine tests',
        items: [
          'Complete blood count (CBC)',
          'Fasting blood glucose',
          'Lipid profile — total cholesterol, HDL, LDL, triglycerides',
          'Kidney function — creatinine, urea',
          'Liver function — AST, ALT',
          'Uric acid',
          'Urinalysis',
        ],
      },
      {
        title: 'Imaging',
        items: ['Chest X-ray (straight)'],
      },
    ],
    genderExtras: {
      male: { title: 'Male option', items: ['Same core programme — male-specific screening is added from the Advanced package'] },
      female: { title: 'Female option', items: ['Gynaecology consultation', 'Clinical breast examination'] },
    },
  },
  {
    slug: 'comprehensive',
    name: 'Comprehensive',
    tier: 2,
    tagline: 'Standard plus deeper testing',
    summary:
      'Everything in Standard, with advanced eye testing, a panoramic dental X-ray, a gastroenterology review and wider metabolic, liver and pancreatic blood work.',
    bestFor: ['Adults 35+', 'Family history of diabetes or liver disease', 'Patients wanting digestive review'],
    duration: 'Approx. half-day',
    highlights: ['All Standard tests', 'Gastroenterology review', 'Metabolic, liver & pancreatic panels'],
    genders: ['male', 'female'],
    includesPrevious: 'Includes everything in Standard',
    sections: [
      {
        title: 'Additional consultations & examinations',
        items: [
          'Advanced eye testing — intraocular pressure and retinal check',
          'Panoramic dental X-ray',
          'Gastroenterology consultation and digestive review',
        ],
      },
      {
        title: 'Wider metabolic, liver & pancreatic testing',
        items: [
          'HbA1c (3-month glucose average)',
          'Extended liver panel — GGT, bilirubin, alkaline phosphatase (ALP)',
          'Pancreatic enzymes — amylase, lipase',
          'Electrolytes — sodium, potassium, chloride',
          'Total protein and albumin',
        ],
      },
    ],
    genderExtras: {
      male: { title: 'Male option', items: ['Urology consultation'] },
      female: { title: 'Female option', items: ['Gynaecology consultation', 'Cervical screening (Pap smear)', 'Breast ultrasound'] },
    },
  },
  {
    slug: 'advanced',
    name: 'Advanced',
    tier: 3,
    tagline: 'Broad screening with ultrasound & ECG',
    summary:
      'Builds on Comprehensive with wider blood and infection screening, hepatitis and thyroid tests, prostate markers, multiple ultrasounds, an ECG and a choice of chest imaging.',
    bestFor: ['Adults 40+', 'Executives and frequent travellers', 'Patients wanting organ-level imaging'],
    duration: 'Half-day to full day',
    highlights: ['Hepatitis & thyroid tests', 'Abdominal, thyroid & urinary ultrasound', 'ECG & selectable chest imaging'],
    genders: ['male', 'female'],
    includesPrevious: 'Includes everything in Comprehensive',
    sections: [
      {
        title: 'Broader blood & infection screening',
        items: [
          'Inflammation markers — ESR, CRP',
          'Infection screening — HIV, syphilis',
          'Hepatitis B — HBsAg, Anti-HBs',
          'Hepatitis C — Anti-HCV',
          'Thyroid function — TSH, FT4',
          'Iron studies and ferritin',
        ],
      },
      {
        title: 'Ultrasound & cardiac',
        items: [
          'Abdominal ultrasound — liver, gallbladder, pancreas, spleen, kidneys',
          'Thyroid ultrasound',
          'Urinary tract ultrasound — kidneys and bladder',
          'Resting ECG (electrocardiogram)',
        ],
      },
      {
        title: 'Selectable chest imaging',
        items: ['Chest X-ray, or', 'Low-dose chest CT (as advised by the doctor)'],
      },
    ],
    genderExtras: {
      male: { title: 'Male option', items: ['PSA / prostate markers (total & free PSA)', 'Prostate ultrasound', 'Urology consultation'] },
      female: { title: 'Female option', items: ['Gynaecology consultation', 'Pelvic / transvaginal ultrasound', 'Breast ultrasound', 'Cervical screening (Pap smear) and HPV test'] },
    },
  },
  {
    slug: 'premium',
    name: 'Premium',
    tier: 4,
    tagline: 'Most complete programme',
    summary:
      'The most complete option — adds cardiology, echocardiography, ABI, lung testing, vitamins and minerals, advanced blood tests, tumour markers, Doppler ultrasound, DEXA bone density and a choice of MRI or CT imaging.',
    bestFor: ['Adults 45+', 'Cardiovascular or cancer family history', 'Patients wanting the widest coverage'],
    duration: 'Full day (results may follow within 1–3 days)',
    highlights: ['Cardiology & echocardiography', 'Tumour markers & DEXA', 'Selectable MRI / CT'],
    genders: ['male', 'female'],
    includesPrevious: 'Includes everything in Advanced',
    sections: [
      {
        title: 'Cardiology & vascular',
        items: [
          'Cardiology consultation',
          'Echocardiography (heart ultrasound)',
          'Ankle-brachial index (ABI)',
          'Doppler ultrasound — carotid arteries',
          'Doppler ultrasound — lower-limb vessels',
        ],
      },
      {
        title: 'Lung, bone & body composition',
        items: [
          'Lung function test (spirometry)',
          'DEXA bone density scan',
        ],
      },
      {
        title: 'Vitamins, minerals & advanced blood tests',
        items: [
          'Vitamin D, vitamin B12, folate',
          'Calcium, magnesium, phosphate, zinc',
          'hs-CRP and homocysteine',
          'Coagulation profile — PT, APTT, INR',
          'Cortisol and additional hormone review where indicated',
        ],
      },
      {
        title: 'Tumour markers',
        items: ['CEA', 'AFP', 'CA 19-9', 'Cyfra 21-1 / NSE (as advised)'],
      },
      {
        title: 'Selectable MRI / CT imaging',
        items: [
          'Brain MRI, or',
          'Low-dose chest CT, or',
          'Abdominal / pelvic MRI or CT (as advised by the doctor)',
        ],
      },
    ],
    genderExtras: {
      male: { title: 'Male option', items: ['PSA / prostate markers', 'Prostate ultrasound', 'Testicular ultrasound where indicated'] },
      female: { title: 'Female option', items: ['Mammogram or breast ultrasound (age-dependent)', 'Pelvic / transvaginal ultrasound', 'CA-125 and CA 15-3 tumour markers', 'Pap smear and HPV test', 'Gynaecology consultation'] },
    },
  },
  {
    slug: 'vip',
    name: 'VIP',
    tier: 5,
    tagline: 'Executive programme with private coordination',
    summary:
      'Everything in Premium, delivered as a private executive programme — senior specialist consultations, advanced cardiac and whole-body imaging, endoscopy under sedation, extended risk screening and a dedicated VietClinics concierge from arrival to results review.',
    bestFor: ['Executives and VIP travellers', 'Patients wanting a private, single-day experience', 'Complex family history needing senior review'],
    duration: 'Full day, private schedule (results review within 1–3 days)',
    highlights: ['Senior specialist consultations', 'Advanced cardiac & whole-body imaging', 'Dedicated concierge & interpreter'],
    genders: ['male', 'female'],
    includesPrevious: 'Includes everything in Premium',
    sections: [
      {
        title: 'Senior consultations & private coordination',
        items: [
          'Consultations with senior specialists (internal medicine, cardiology, gastroenterology)',
          'Dedicated VietClinics concierge and medical interpreter throughout the day',
          'Private lounge or fast-track scheduling where the hospital offers it',
          'Results review consultation and written summary in English',
          'Follow-up online consultation after results',
        ],
      },
      {
        title: 'Advanced cardiac & whole-body imaging',
        items: [
          'Coronary CT angiography or cardiac MRI (selectable, as advised)',
          'Whole-body MRI screening (as advised)',
          'Carotid and peripheral Doppler ultrasound',
          'Extended echocardiography with strain assessment where available',
        ],
      },
      {
        title: 'Endoscopy & extended screening',
        items: [
          'Gastroscopy and colonoscopy under sedation (as advised)',
          'Extended tumour marker panel',
          'Hereditary / genetic cancer-risk screening (as advised)',
          'Sleep, stress and metabolic risk review',
          'Extended hormone and vitamin panel',
        ],
      },
    ],
    genderExtras: {
      male: { title: 'Male option', items: ['Multiparametric prostate MRI where indicated', 'PSA / prostate markers and prostate ultrasound', 'Testosterone and male hormone panel'] },
      female: { title: 'Female option', items: ['Mammogram and breast ultrasound', 'Pelvic MRI or transvaginal ultrasound (as advised)', 'CA-125, CA 15-3 and HE4 markers', 'Pap smear and HPV test', 'Female hormone and bone-health panel'] },
    },
  },
];
